import {Campaign} from "@/models/campaign";
import {ResourceQuery, useResourceQuery} from "@/graphql/API";
import {ApolloError, ApolloQueryResult} from "@apollo/client";
import {Helper} from "@/utils/helper";
import {SurveyState} from "@/views/survey/SurveyView.vue";

// TODO: Remove when recorder is moved to new design
import {RecorderState} from "@/views/recorder/LegacyRecorderView.vue";

export const ResourceNotFoundError = "Resource Not Found";

export class CampaignFetcher {
    private readonly isPreview: boolean;
    private readonly url?: string;

    constructor(isPreview: boolean, url?: string) {
        this.isPreview = isPreview;
        this.url = url;
    }

    // TODO: Remove when recorder is moved to new design - remove RecorderState from callback
    listen(callback: (campaign?: Campaign, state?: SurveyState | RecorderState, error?: boolean) => void): void {
        if (this.isPreview) {
            window.addEventListener('message', (event) => {
                if (!event) {
                    console.log("Ignoring message because no event");
                    return;
                }

                if (event.origin !== 'http://localhost:3000' && event.origin !== 'https://beams.fm' && event.origin !== 'https://alpha.b3ams.com' && event.origin !== 'https://simonpal.eu.ngrok.io') {
                    console.log("Ignoring message from parent as it does not come from a trusted origin");
                    return;
                }

                const campaign: Campaign | undefined = event.data.campaign as Campaign;
                if (!campaign) {
                    console.error("Ignoring message from parent as it does not contain a campaign");
                } else {
                    callback(campaign, event.data.state, false);
                }
            });
        } else {
            this.fetchCampaign()
                .then((campaign) => {
                    callback(campaign, undefined, false);
                })
                .catch((error) => {
                    console.error("Error while fetching campaign", error);
                    callback(undefined, undefined, true);
                });
        }
    }

    private fetchCampaign(): Promise<Campaign> {
        return new Promise((resolve, reject) => {
            if (!this.url) {
                return reject(Error("No URL provided for campaign fetch"))
            }

            const {onResult, onError} = useResourceQuery({url: this.url});

            onResult((result: ApolloQueryResult<ResourceQuery>) => {
                if (!result || !result.data) {
                    return reject(Error("Result object for campaign fetch unexpectedly empty"))
                }

                if (!result.data.resourceV2.resource) {
                    return reject(ResourceNotFoundError);
                }

                if (result.data.resourceV2.resource?.__typename !== "ResourceCampaignContributionResponse") {
                    return reject(Error("Unexpected resource fetch return type (expected campaign contribution)"))
                }

                const campaign: Campaign | undefined = result.data.resourceV2.resource as unknown as Campaign;
                if (!campaign) {
                    return reject(new Error("campaign key missing in ResourceCampaignContributionResponse"))
                }

                resolve(campaign);
            });

            onError((error: ApolloError) => {
                if (!window.Cypress) {
                    Helper.sendMessageToParentWindow({name: "Beams::ResourceLoadingError"});
                }

                if (error.message.includes("[404]")) {
                    console.error("Campaign not found.")
                    return reject(ResourceNotFoundError);
                } else {
                    console.error("Error fetching the resource: " + error.message)
                    return reject(error.message);
                }
            });
        });
    }
}
